// @ts-nocheck

import { debounce, filter, some, find } from 'lodash';

import Formatter from '../../../i18n/formatter';
import { __, getCurrency } from '../../../i18n/l10n';
import storage from '../../../utils/storage';
import { GRID_STORAGE_KEY } from '../constants';
import { GridsCheckTypes, GridsErrorCodes } from '../types';
import type { BasketState, GridsPoolState as PoolType } from '../types';

import Pool from './pool';
import Selection from './selection';

type ResponseType = {
  ecode: number;
  einfo: {
    max: number;
  };
  estr?: string;
};

interface IBasket {
  loadFromStorage: (poolId: number) => BasketState | null;
  checkBasketIsValid: (basket: BasketState, pool: PoolType) => boolean;
  saveToStorage: (basket: BasketState, pools: PoolType[]) => void;
  getErrorCodeMessage: (response: ResponseType) => string;
  hasAtLeastOneDoubleOrOneTriple: (selections: Array<string>) => boolean;
}

class Basket implements IBasket {
  static loadFromStorage = (poolId: number) => {
    const savedPools = storage.sessionStorage.load(GRID_STORAGE_KEY);
    if (savedPools && savedPools[poolId]) {
      return savedPools[poolId];
    }
    return null;
  };

  static checkBasketIsValid = (basket: BasketState, pool: PoolType) => {
    if (pool) {
      const { matches } = pool;
      const { selections } = basket;
      const requiredSelectionLength = matches.length * Object.keys(GridsCheckTypes).length;
      const wrongFormatSelections = some(selections, (selection) => selection.length !== requiredSelectionLength);
      return !wrongFormatSelections;
    }
    return false;
  };

  static saveToStorage = debounce((basket, pools) => {
    let data = {};
    const savedPools = storage.sessionStorage.load(GRID_STORAGE_KEY);
    const openPools = filter(pools, (pool) => Pool.isOpen(pool));
    // we remove closed pools
    if (savedPools) {
      const openPoolIds = openPools.map((pool) => pool.poolId);
      data = Object.keys(savedPools).reduce(
        (acc, poolId) =>
          openPoolIds.indexOf(parseInt(poolId, 10)) !== -1 ? { ...acc, [`${poolId}`]: savedPools[poolId] } : acc,
        {},
      );
    }
    // we add current basket
    const { poolId } = basket;
    const pool = find(openPools, (pool) => pool.poolId === poolId);
    if (Basket.checkBasketIsValid(basket, pool)) {
      data[poolId] = basket;
    }
    storage.sessionStorage.save(GRID_STORAGE_KEY, data);
  }, 300);

  static getErrorCodeMessage = ({ ecode, einfo, estr }: ResponseType): string => {
    let message = estr || __('Internal error');
    switch (ecode) {
      case GridsErrorCodes.ACCESS_DENIED: {
        message = __('You must be logged in to validate your grid.');
        break;
      }
      case GridsErrorCodes.NOT_ENOUGH_MONEY: {
        if (einfo) {
          if (einfo.max === 0) {
            message = __('You have no funds in your account.');
          } else {
            const amount = parseFloat(einfo.max / 100);
            message = __(
              `You only have %1$s euros available on your account`,
              Formatter.formatNumber(amount, getCurrency()),
            );
          }
        } else {
          message = __('You have no funds in your account.');
        }
        break;
      }
      case GridsErrorCodes.INVALID_BET_AMOUNT: {
        message = __('Please enter a valid amount.');
        break;
      }
      case GridsErrorCodes.MULTIPLE_CHECKS_REQUIRED: {
        message = __('You must select doubles or triples to make a team grid.');
        break;
      }
      case GridsErrorCodes.MODERATOR_REACHED:
      case GridsErrorCodes.DAILY_MODERATOR_REACHED:
      case GridsErrorCodes.WEEKLY_MODERATOR_REACHED:
      case GridsErrorCodes.MONTHLY_MODERATOR_REACHED: {
        if (einfo?.max !== undefined && !isNaN(einfo.max)) {
          const amount = parseFloat(einfo.max / 100);
          const amountLabel = Formatter.formatNumber(amount, getCurrency());
          if (ecode === GridsErrorCodes.DAILY_MODERATOR_REACHED) {
            message = __('You will exceed your daily limit. The maximum wager is %1$s', amountLabel);
          } else if (ecode === GridsErrorCodes.WEEKLY_MODERATOR_REACHED) {
            message = __('You will exceed your weekly limit. The maximum wager is %1$s', amountLabel);
          } else if (ecode === GridsErrorCodes.MONTHLY_MODERATOR_REACHED) {
            message = __('You will exceed your monthly limit. The maximum wager is %1$s', amountLabel);
          }
        }
        break;
      }
      case GridsErrorCodes.GRID_NOT_AVAILABLE: {
        message = __(`You cannot bet on this grid anymore.`);
        break;
      }
      case GridsErrorCodes.GRID_INCOMPLETE: {
        message = __('You must tick at least one box per line.');
        break;
      }
      default:
        break;
    }
    return message;
  };

  static hasAtLeastOneDoubleOrOneTriple = (selections: Array<string>): boolean => {
    if (selections && selections.length) {
      return some(
        selections,
        (selection) => Selection.getCountDoubles(selection) || Selection.getCountTriples(selection),
      );
    }
    return false;
  };
}

export default Basket;
